import {
  DatePicker,
  fromDate,
  getLocalTimeZone,
  NumberField,
  SelectField,
  toCalendarDate,
} from '@wandb/components';
import React, {type FC, useState} from 'react';

import * as Filter from '../../util/filters';
import type * as FilterTypes from '../../util/filterTypes';

interface FilterValueSelectorDateProps {
  value: string | null;
  setFilter(
    filter: Partial<FilterTypes.IndividualFilter<FilterTypes.FilterKey>>
  ): void;
}

/**
 * The stored value is an ISO timestamp for local midnight of the chosen day,
 * which is what the native date input this replaces produced. The picker
 * works in calendar days in the browser's time zone, so convert at the edges.
 */
export const FilterValueSelectorDate: FC<FilterValueSelectorDateProps> =
  React.memo(({value, setFilter}) => (
    // Sized like the other value fields: fills the rest of the row, up to
    // the shared cap.
    <div className="min-w-0 flex-grow">
      <DatePicker
        aria-label="Date"
        granularity="day"
        data-test="filter-value"
        fullWidth
        value={toCalendarDate(
          fromDate(value ? new Date(value) : new Date(), getLocalTimeZone())
        )}
        onChange={day => {
          if (day == null) {
            return;
          }
          setFilter({
            value: toCalendarDate(day).toDate(getLocalTimeZone()).toISOString(),
            disabled: false,
          });
        }}
      />
    </div>
  ));

// Sets any unit to seconds
const toSeconds = (value: string, unit: FilterTypes.TimeOption) => {
  return (
    parseFloat(value) * Filter.checkedGetTimeOptionData(unit).numSeconds
  ).toString();
};

// Sets seconds to units
const toUnit = (secondsValue: string, unit: FilterTypes.TimeOption): string => {
  return (
    parseFloat(secondsValue) / Filter.checkedGetTimeOptionData(unit).numSeconds
  ).toString();
};

// Convert n units(from) : n units(to)
// Keeping n the same but adjusting units
export const convert = (
  value: string,
  from: FilterTypes.TimeOption,
  to: FilterTypes.TimeOption
): string => {
  const initialValue = parseFloat(value);
  return (
    (initialValue / Filter.checkedGetTimeOptionData(from).numSeconds) *
    Filter.checkedGetTimeOptionData(to).numSeconds
  ).toString();
};

const getOptionsList = (minimumUnit: FilterTypes.TimeOption | null) =>
  Filter.getSortedTimeOptions(minimumUnit).map(([unit, data]) => ({
    id: unit,
    label: data.displayText,
  }));

// Durations were free text before; keep fractional amounts and skip the
// thousands separators Intl adds by default.
const DURATION_FORMAT = {useGrouping: false, maximumFractionDigits: 6} as const;

interface FilterValueSelectorTimeProps {
  value: string | null;
  meta?: FilterTypes.FilterMeta;
  minimumUnit: FilterTypes.TimeOption | null;
  setFilter(
    filter: Partial<FilterTypes.IndividualFilter<FilterTypes.FilterKey>>
  ): void;
}

export const FilterValueSelectorTime: FC<FilterValueSelectorTimeProps> =
  React.memo(({value, meta, minimumUnit, setFilter}) => {
    const defaultUnit = minimumUnit || 'seconds';
    const unit: FilterTypes.TimeOption = meta ? meta.unit : defaultUnit;
    const val = value || '0';
    const [displayValue, setDisplayValue] = useState<number>(() =>
      parseFloat(toUnit(val, unit))
    );

    return (
      <React.Fragment>
        <NumberField
          aria-label="Duration"
          data-test="filter-value"
          compact
          minValue={0}
          formatOptions={DURATION_FORMAT}
          value={displayValue}
          // The field commits on blur, Enter, and the stepper buttons, so
          // this fires once per edit rather than per keystroke. An emptied
          // field reads as NaN and falls back to 0 like the old input did.
          onChange={next => {
            const amount = Number.isNaN(next) ? 0 : next;
            setDisplayValue(amount);
            const filterValue = toSeconds(String(amount), unit);
            if (filterValue !== val) {
              setFilter({value: filterValue});
            }
          }}
        />
        <SelectField
          aria-label="Time unit"
          data-test="filter-value-unit"
          items={getOptionsList(minimumUnit)}
          value={unit}
          onChange={unitValue => {
            if (unitValue == null) {
              return;
            }
            const nextUnit = unitValue as FilterTypes.TimeOption;
            setFilter({
              value: convert(val, unit, nextUnit),
              meta: {unit: nextUnit},
              disabled: false,
            });
          }}
        />
      </React.Fragment>
    );
  });

export interface FilterValueSelectorCreatorProps<
  K extends FilterTypes.FilterKey,
> {
  filter: FilterTypes.IndividualFilter<K>;
  setFilter(filter: FilterTypes.IndividualFilter<K>): void;
  isValid?: boolean;
}
